import { Button, Drawer, Form, Input, Radio, Steps, Tooltip, Typography } from 'antd';
import { connect } from 'react-redux';
import { getResponseFromOpenAPI } from '../../thunks/open_api_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { useForm } from 'antd/lib/form/Form';
import { LoadingOutlined } from '@ant-design/icons';
import React, { useEffect, useState, FC } from 'react';
import OWTaskComponent from './OWTaskComponent';
import { doNothingPromise } from '../../thunks/fetch_utils';
import OWTaskFormComponent from './OWTaskFormComponent';

const industry = 'Fitness and Recreation';

const sanitiseChangedTasks = (tasks) => {
	const sanitizedTasks = (tasks || []).filter((_) => !!_.title);
	return sanitizedTasks.map((task, orderingId) => ({
		title: task.title,
		questionType: task.questionType,
		groupTitle: task.groupTitle,
		orderingId,
	}));
};

const OWTaskFormWithModalComponent: FC<any> = ({
	value,
	onChange,
	getAIResponse,
	PMTemplate,
}): React.ReactElement => {
	const [showAIModal, setShowAIModal] = useState(false);
	const [inputText, setInputText] = useState('');
	const [aiTasks, setAITasks] = useState([]);
	const [loading, setLoading] = useState(false);
	const [form] = useForm();

	const sendToOpenAI = async (values: any) => {
		setLoading(true);

		const systemMessage =
			processingMode === 'formatasis'
				? `You are a CMMS expert specialized in maintenance tasks for the Fitness and Recreation industry. 
        You understand the specific maintenance requirements, compliance standards, and best practices for this sector. 
        Format the provided task list exactly as given, without adding or modifying content. 
        IMPORTANT: Extract ALL tasks and subtasks while maintaining their EXACT ORIGINAL ORDER in the sequence. 
        CRITICAL GROUPING RULE: Tasks should ONLY be placed in the same group (using the same groupTitle) if they have CONSECUTIVE ordering numbers. 
        ORDER ID REQUIREMENT: The orderingId field must be a positive integer (1, 2, 3, etc.). 
        Never use decimals or negative numbers. 
        Return only a JSON response matching this interface:
        interface Task {
          title: string;
          questionType: checkbox;
          groupTitle: string;
          orderingId: number;
        }
        interface ResponseSignature {
          taskCompletionMandatory: boolean;
          tasks: Task[];
        }`
				: values.detailLevel === 'brief'
				? `"You are a CMMS expert specialized in maintenance tasks for the ${industry} industry. "
            "You understand the specific maintenance requirements, compliance standards, and best practices for this sector. "
            "Create a detailed task list based on the provided information, ensuring it meets ${industry} industry standards. "
            "IMPORTANT: Include ALL detailed steps and subtasks in your response while maintaining their logical sequence. "
            "CRITICAL GROUPING RULE: Tasks should ONLY be placed in the same group (using the same groupTitle) if they have "
            "CONSECUTIVE ordering numbers. For example, if tasks 1, 14, 15 are logically related, they should be in DIFFERENT "
            "groups (like 'Safety Check - Start' and 'Safety Check - End') because they are not consecutive. "
            "Generate a terse list with 4-5 groups containing 2-3 tasks each.\n\n"
            "Return only a JSON response matching this interface: \n"
            "interface Task {{\n"
            "  title: string;\n"
            "  questionType: checkbox;\n"
            "  groupTitle: string;  // can be empty string if no group\n"
            "  orderingId: number;\n"
            "}}\n"
            "interface ResponseSignature {{\n"
            "  taskCompletionMandatory: boolean;\n"
            "  tasks: Task[];\n"
            "}}\n"`
				: `"You are a CMMS expert specialized in maintenance tasks for the ${industry} industry. "
            "You understand the specific maintenance requirements, compliance standards, and best practices for this sector. "
            "Create a detailed task list based on the provided information, ensuring it meets ${industry} industry standards. "
            "IMPORTANT: Extract and include ALL detailed steps and subtasks while preserving their EXACT ORIGINAL SEQUENCE. "
            "CRITICAL GROUPING RULE: Tasks should ONLY be placed in the same group (using the same groupTitle) if they have "
            "CONSECUTIVE ordering numbers. For example, if tasks 1, 14, 15 are logically related, they should be in DIFFERENT "
            "groups (like 'Cleaning - Start' and 'Cleaning - End') because they are not consecutive. "
            "ORDER ID REQUIREMENT: The orderingId field must be a positive integer (1, 2, 3, etc.). Never use decimals or negative numbers. "
            "Generate a detailed list with 7-8 groups containing 4+ tasks each.\n\n"
            "Return only a JSON response matching this interface: \n"
            "interface Task {{\n"
            "  title: string;\n"
            "  questionType: checkbox;\n"
            "  groupTitle: string;  // can be empty string if no group\n"
            "  orderingId: number;  // Must be a positive integer (1, 2, 3, etc.)\n"
            "}}\n"
            "interface ResponseSignature {{\n"
            "  taskCompletionMandatory: boolean;\n"
            "  tasks: Task[];\n"
            "}}\n"`;

		const userMessage = `
      Industry: Fitness and Recreation
      PM Title: ${nullSafeGet('title', PMTemplate)}
      Task Information: ${values.taskList}
    `;

		try {
			const temperature =
				processingMode === 'formatasis' ? 0.1 : values.detailLevel === 'brief' ? 0.5 : 0.7;
			const data = await getAIResponse({
				question: {
					model: processingMode === 'formatasis' ? 'gpt-4o' : 'gpt-4o',
					messages: [
						{ role: 'system', content: systemMessage },
						{ role: 'user', content: userMessage },
					],
					temperature,
				},
			});

			const cleanedResponse = nullSafeGetOrElse('title', data, '')
				.replaceAll('\n', '')
				.replaceAll('```json', '')
				.replaceAll('```', '');

			const parsedTasks = JSON.parse(cleanedResponse).tasks;

			setAITasks(parsedTasks);
		} catch (error) {
			console.error('Error fetching OpenAI response:', error);
		} finally {
			setLoading(false);
		}
	};

	const processingMode = Form.useWatch('processingMode', form);

	return (
		<div>
			<div className=" flex w-full flex-row items-center justify-between">
				<div className="ant-form-item-label">Tasks</div>
				<Tooltip title="Generate with Openwrench AI">
					<Button onClick={() => setShowAIModal(true)} className="items-baseline ">
						<svg
							className="fill-indigo-600"
							viewBox="0 0 14 14"
							width="14"
							height="14"
							style={{ marginRight: 8 }}
						>
							<path
								fillRule="evenodd"
								d="M5.25 2.625a.437.437 0 01.42.317l.474 1.662a2.25 2.25 0 001.546 1.546l1.662.474a.437.437 0 010 .84l-1.662.474a2.25 2.25 0 00-1.546 1.546l-.474 1.662a.437.437 0 01-.84 0l-.474-1.662a2.25 2.25 0 00-1.546-1.546l-1.662-.474a.437.437 0 010-.84l1.662-.474A2.25 2.25 0 004.38 4.604l.474-1.662a.437.437 0 01.396-.317zM10.5 .875a.437.437 0 01.425.331l.151.605c.14.56.582 1.002 1.142 1.142l.605.151a.437.437 0 010 .849l-.605.151a1.575 1.575 0 00-1.142 1.142l-.151.605a.437.437 0 01-.849 0l-.151-.605a1.575 1.575 0 00-1.142-1.142l-.605-.151a.437.437 0 010-.849l.605-.151c.56-.14 1.002-.582 1.142-1.142l.151-.605A.437.437 0 0110.5.875zM9.625 8.75a.437.437 0 01.415.298l.23.69c.088.26.3.466.568.568l.69.23a.437.437 0 010 .828l-.69.23a.875.875 0 00-.568.568l-.23.69a.437.437 0 01-.828 0l-.23-.69a.875.875 0 00-.568-.568l-.69-.23a.437.437 0 010-.828l.69-.23c.26-.088.466-.3.568-.568l.23-.69a.437.437 0 01.415-.298z"
								clipRule="evenodd"
							></path>
						</svg>
						Generate with AI
					</Button>
				</Tooltip>
			</div>
			{showAIModal ? (
				<LoadingOutlined />
			) : (
				<OWTaskFormComponent value={value} onChange={onChange} />
			)}
			<Drawer
				visible={showAIModal}
				onClose={() => setShowAIModal(false)}
				title="Generate with Openwrench AI"
				footer={null}
				width={'100vw'}
			>
				<div className=" flex h-full flex-row gap-4">
					<div className=" mx-4 flex flex-1 flex-col  ">
						{nullSafeGet('title', PMTemplate) ? (
							<Typography.Title level={3}>{nullSafeGet('title', PMTemplate)}</Typography.Title>
						) : null}
						<Typography.Paragraph>
							Welcome to OpenWrench AI, Here Specify the details for which maintenance tasks need to
							be generated. This will be used to fetch relevant tasks from the system.
						</Typography.Paragraph>

						<Form form={form} layout="vertical" onFinish={sendToOpenAI}>
							<Form.Item
								name="processingMode"
								label="Processing Mode"
								initialValue={'formatasis'}
								rules={[{ required: true, message: 'Required' }]}
							>
								<Radio.Group>
									<Radio.Button value={'formatasis'}>Format As Is</Radio.Button>
									<Radio.Button value={'generate'}>Generate Tasks</Radio.Button>
								</Radio.Group>
							</Form.Item>
							{processingMode === 'generate' && (
								<Form.Item
									name="detailLevel"
									label="Detail Level"
									rules={[{ required: true, message: 'Required' }]}
								>
									<Radio.Group>
										<Radio value={'brief'}>Brief (4-5 groups, 2-3 tasks each)</Radio>
										<Radio value={'detailed'}>Detailed (7-8 groups, 4+ tasks each)</Radio>
									</Radio.Group>
								</Form.Item>
							)}

							<Form.Item
								name="taskList"
								label="Description"
								rules={[{ required: true, message: 'Required' }]}
							>
								<Input.TextArea
									onChange={(e) => setInputText(e.target.value)}
									rows={6}
									placeholder={
										processingMode === 'formatasis'
											? `PLATED EQUIPMENT INSPECTION
1. Make sure machine is level and not rocking. If rocking, adjust feet until unit is level.
2. Inspect condition of shrouds and decals.
3. Inspect the machine for any loose bolts or screws. If any are loose tighten.
4. Inspect the frame welds. Frames hold at least 5 years of warranty so any deficiencies should be a warranty replacement.
5. Inspect the condition of the upholstery. Upholstery should be like-new with no rips or tears.`
											: 'E.g. Generate tasks for the weight area items include plated equipment, plate racks, dumbells, barbells and squat racks etc.'
									}
									value={inputText}
								/>
							</Form.Item>

							<Button loading={loading} type="primary" className="mt-2" htmlType="submit">
								{nullSafeGet('length', aiTasks) > 0 ? 'Regenerate' : 'Generate'}
							</Button>
						</Form>
					</div>
					<div className="flex-1 px-4">
						{loading ? (
							<Loader processingMode={processingMode} />
						) : (
							nullSafeGet('length', aiTasks) > 0 && (
								<>
									<Typography.Title level={5}>
										Here you go, you can add/edit these tasks{' '}
									</Typography.Title>

									<div style={{ marginTop: 16, padding: 16 }}>
										<OWTaskComponent
											existingTasks={aiTasks}
											isAllowedToEdit
											bulkUpdateTasks={doNothingPromise}
											bulkDeleteTasks={doNothingPromise}
											onTaskEdit={doNothingPromise}
											onTaskDelete={doNothingPromise}
											timezonId={null}
											onTasksChange={(tasks = []) => {
												setAITasks(sanitiseChangedTasks(tasks));
											}}
											notCheckable
											isPMTemplate
										/>
										<Button
											type="primary"
											onClick={() => {
												onChange(aiTasks);
												setShowAIModal(false);
											}}
											className="mt-4"
										>
											Save
										</Button>
									</div>
								</>
							)
						)}
					</div>
				</div>
			</Drawer>
		</div>
	);
};

const mapStateToProps = (state) => ({
	// map state to props
});

const mapDispatchToProps = (dispatch) => ({
	getAIResponse: (entity) => dispatch(getResponseFromOpenAPI('buyer')(entity)),
});

export const OWTaskFormComponentWithAI = connect(
	mapStateToProps,
	mapDispatchToProps
)(OWTaskFormWithModalComponent);

function Loader({ processingMode }) {
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrent((prev) => (prev < items.length - 1 ? prev + 1 : prev));
		}, 10000);

		return () => clearInterval(interval);
	}, []);

	const items =
		processingMode === 'formatasis'
			? [
					{ title: 'Extracting data as is from the text' },
					{ title: 'Cleaning up any bad formatting' },
					{ title: 'Checking for any groupings' },
					{ title: 'Finalizing' },
			  ]
			: [
					{ title: 'Understanding context and intent' },
					{ title: 'Retrieving relevant information' },
					{ title: 'Identifying all relevant tasks' },
					{ title: 'Identifying any grouping' },
					{ title: 'Finalizing' },
			  ];

	return (
		<div className="flex h-full w-full items-center justify-center">
			<Steps
				direction="vertical"
				size="default"
				current={current}
				style={{ marginLeft: '20%', height: '50%' }}
			>
				{items.map((item, index) => (
					<Steps.Step
						key={index}
						title={item.title}
						status={index < current ? 'finish' : index === current ? 'process' : 'wait'}
						icon={index === current ? <LoadingOutlined /> : null}
					/>
				))}
			</Steps>
		</div>
	);
}
